import React, { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import { Card, Button, Spinner } from "../../../../shared";
import {
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useUpdateLeadAssigneeMutation,
  useCreateCustomerFromLeadMutation,
} from "../../../../redux/services/leads/leadsApi";
import { useAuth } from "../../../../hooks/useAuth";

import StatusPill from "../StatusPill";
import { STATUS_FILTER_OPTIONS } from "../table.config";
import buildLeadReplyMailto, {
  getLeadsCalendlyUrl,
} from "../../../../utils/buildLeadReplyMailto";
import NotesThread from "./NotesThread";
import ActivityLog from "./ActivityLog";

/**
 * Detail page for a single Lead. Top header surfaces status,
 * assignee, and the "Reply" mailto button. Below the header sits the
 * read-only Submission card, the editable NotesThread, and the
 * append-only ActivityLog timeline. Status + assignee mutations write
 * directly through RTK Query so the activity log stays in sync.
 */
const LeadShow = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isFetching } = useGetLeadQuery(
    { id: params.id },
    { skip: !params.id }
  );

  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateLeadStatusMutation();
  const [updateAssignee, { isLoading: isUpdatingAssignee }] =
    useUpdateLeadAssigneeMutation();
  const [createCustomerFromLead, { isLoading: isCreatingCustomer }] =
    useCreateCustomerFromLeadMutation();

  const lead = data || null;

  /** Populated customer ref or raw id after conversion */
  const convertedCustomerId =
    lead?.convertedCustomerId &&
    (lead.convertedCustomerId._id || lead.convertedCustomerId);

  const calendlyUrl = useMemo(() => getLeadsCalendlyUrl(), []);
  const mailtoHref = useMemo(() => buildLeadReplyMailto(lead), [lead]);

  const statusOptions = useMemo(
    () => STATUS_FILTER_OPTIONS.filter((opt) => opt.value),
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-6">
        <Button type="alternative" onClick={() => navigate("/admin/leads")}>
          <ArrowLeftIcon className="w-4 h-4 mr-1 inline" /> Back to leads
        </Button>
        <p className="mt-4 text-gray-500">Lead not found.</p>
      </div>
    );
  }

  const handleStatusChange = async (event) => {
    const next = event.target.value;
    if (!next || next === lead.status) return;
    try {
      await updateStatus({ id: lead._id, status: next }).unwrap();
      toast("Lead status updated", { type: "success" });
    } catch (err) {
      toast(err?.data?.message || "Unable to update status", {
        type: "error",
      });
    }
  };

  const handleAssignToMe = async () => {
    if (!user?._id) return;
    try {
      await updateAssignee({
        id: lead._id,
        assignedTo: user._id,
      }).unwrap();
      toast("Lead assigned", { type: "success" });
    } catch (err) {
      toast(err?.data?.message || "Unable to assign lead", { type: "error" });
    }
  };

  const handleUnassign = async () => {
    try {
      await updateAssignee({ id: lead._id, assignedTo: null }).unwrap();
      toast("Lead unassigned", { type: "success" });
    } catch (err) {
      toast(err?.data?.message || "Unable to unassign lead", {
        type: "error",
      });
    }
  };

  /**
   * Runs the same onboarding pipeline as Super Admin → Create customer,
   * using company + contact fields from the won lead.
   */
  const handleCreateCustomerFromLead = async () => {
    try {
      await createCustomerFromLead({ id: lead._id }).unwrap();
      toast(
        "Customer created. The owner will receive the onboarding email.",
        { type: "success" }
      );
    } catch (err) {
      toast(err?.data?.message || "Unable to create customer from this lead", {
        type: "error",
      });
    }
  };

  const assignee = lead.assignedTo || null;
  const assigneeLabel = assignee
    ? `${assignee.firstName || ""} ${assignee.lastName || ""}`.trim() ||
      assignee.email ||
      "Assigned"
    : "Unassigned";
  const isAssignedToMe = !!assignee && user?._id === assignee._id;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center text-sm text-gray-500 gap-1">
        <Link to="/admin/leads" className="hover:underline">
          Leads
        </Link>
        <ChevronRightIcon className="w-4 h-4" />
        <span className="text-gray-700 font-medium">
          {lead.companyName || lead.contactName || lead.email}
        </span>
      </div>

      <Card className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold text-gray-900">
              {lead.companyName || "(No company name)"}
            </h1>
            <div className="text-sm text-gray-600">
              {lead.contactName || "(No contact name)"}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
              {lead.email && (
                <a
                  href={`mailto:${lead.email}`}
                  className="inline-flex items-center gap-1 hover:text-primary-600"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  {lead.email}
                </a>
              )}
              {lead.phone && (
                <a
                  href={`tel:${lead.phone}`}
                  className="inline-flex items-center gap-1 hover:text-primary-600"
                >
                  <PhoneIcon className="w-4 h-4" />
                  {lead.phone}
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 items-stretch md:items-end">
            <div className="flex items-center gap-2">
              <StatusPill status={lead.status} />
              <select
                value={lead.status || "new"}
                onChange={handleStatusChange}
                disabled={isUpdatingStatus || isFetching}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Assignee:</span>
              <span className="font-medium text-gray-700">
                {assigneeLabel}
              </span>
              {isAssignedToMe ? (
                <Button
                  size="xs"
                  type="alternative"
                  onClick={handleUnassign}
                  disabled={isUpdatingAssignee}
                >
                  Unassign
                </Button>
              ) : (
                <Button
                  size="xs"
                  type="alternative"
                  onClick={handleAssignToMe}
                  disabled={isUpdatingAssignee || !user?._id}
                >
                  {assignee ? "Take over" : "Assign to me"}
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-2 items-stretch md:items-end">
              {lead.status === "won" && convertedCustomerId && (
                <Link to={`/admin/customers/${convertedCustomerId}`}>
                  <Button size="sm" type="alternative" className="w-full md:w-auto">
                    Open customer record
                  </Button>
                </Link>
              )}
              {lead.status === "won" &&
                !convertedCustomerId &&
                lead.phone &&
                lead.email &&
                lead.companyName && (
                  <Button
                    size="sm"
                    type="primary"
                    loading={isCreatingCustomer}
                    disabled={
                      isCreatingCustomer ||
                      isFetching ||
                      isUpdatingStatus ||
                      isUpdatingAssignee
                    }
                    onClick={handleCreateCustomerFromLead}
                    className="w-full md:w-auto"
                  >
                    Create customer from lead
                  </Button>
                )}
              {lead.status === "won" &&
                !convertedCustomerId &&
                (!lead.phone || !lead.email || !lead.companyName) && (
                  <p className="text-xs text-amber-700 text-right max-w-xs">
                    Add company name, email, and phone on this lead before
                    creating a customer (required for onboarding).
                  </p>
                )}
              <div className="flex justify-end">
                {mailtoHref ? (
                  <a href={mailtoHref}>
                    <Button size="sm" type="primary">
                      Reply via email
                    </Button>
                  </a>
                ) : (
                  <Button size="sm" type="primary" disabled>
                    Reply via email
                  </Button>
                )}
              </div>
            </div>
            {!calendlyUrl && (
              <p className="text-xs text-gray-400 max-w-xs text-right">
                Tip: set <code>VITE_LEADS_CALENDLY_URL</code> to drop a booking link
                into reply emails automatically.
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card title="Submission">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <SubmissionRow label="Submitted" value={formatDate(lead.dateCreated)} />
          <SubmissionRow label="Source type" value={lead.type || "contact_form"} />
          <SubmissionRow label="Industry" value={lead.industry || "—"} />
          <SubmissionRow label="Company size" value={lead.companySize || "—"} />
          <SubmissionRow
            label="Referer"
            value={lead.meta?.referer || "—"}
            mono
          />
          <SubmissionRow
            label="User agent"
            value={lead.meta?.userAgent || "—"}
            mono
          />
        </dl>

        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">
            Message
          </h4>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {lead.message || "(no message provided)"}
          </p>
        </div>
      </Card>

      <NotesThread leadId={lead._id} notes={lead.notes || []} />

      <ActivityLog activityLog={lead.activityLog || []} />
    </div>
  );
};

const SubmissionRow = ({ label, value, mono }) => (
  <div className="flex flex-col">
    <dt className="text-xs font-semibold uppercase text-gray-500">{label}</dt>
    <dd
      className={`text-sm text-gray-800 break-words ${
        mono ? "font-mono text-xs" : ""
      }`}
    >
      {value}
    </dd>
  </div>
);

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

export default LeadShow;
